import { DragEndEvent, DragStartEvent, useDndMonitor } from '@dnd-kit/core';
import React, { useState, ReactNode, useCallback } from 'react';

import { Menu } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { MenuEditorDragEventData } from 'modules/menu/components/MenuEditorDnDContext';
import { getMenuEditorDragEventData } from 'modules/menu/components/MenuEditorDnDContext/utils';
import { MenuEditorElement } from 'modules/menu/types';

type MenuItemsDnDMonitorProps = {
  menu: Menu;
  menuEditorList: MenuEditorElement[];
  children: (data: { draggingId?: string; displayMenuEditorList: MenuEditorElement[] }) => ReactNode;
  onMoveItemsToCategory?: (activeId: string, overId: string, selectedIds?: Set<string>) => Promise<void>;
  onAssignNoCategoryItemsToCategory?: (activeId: string, overId: string, selectedIds?: Set<string>) => Promise<void>;
  onMoveItemToNoCategory?: (activeId: string) => Promise<void>;
};

export const MenuItemsDnDMonitor = ({
  children,
  onMoveItemsToCategory,
  onMoveItemToNoCategory,
  onAssignNoCategoryItemsToCategory,
  menuEditorList,
}: MenuItemsDnDMonitorProps) => {
  // Use cached active due to the issue with virtualized lists cause the active.data.current to get lost: https://github.com/clauderic/dnd-kit/issues/794
  const [activeEvent, setActiveEvent] = useState<null | MenuEditorDragEventData>(null);
  const [localMenuEditorList, setLocalMenuEditorList] = useState<null | MenuEditorElement[]>();

  const reset = useCallback(() => {
    setActiveEvent(null);
    setLocalMenuEditorList(null);
  }, []);

  const onDragStart = useCallback(
    ({ active }: DragStartEvent) => {
      const activeEvent = active && getMenuEditorDragEventData(active);

      if (activeEvent) {
        setActiveEvent(activeEvent);
        setLocalMenuEditorList(menuEditorList);
      }
    },
    [menuEditorList],
  );

  const onDragEnd = useCallback(
    async ({ over }: DragEndEvent) => {
      try {
        const overEvent = over && getMenuEditorDragEventData(over);
        if (!overEvent || !activeEvent) {
          return;
        }

        setLocalMenuEditorList(state =>
          state?.filter(list => (list.type === 'item' ? list.itemState.id !== activeEvent.id : true)),
        );

        if (
          activeEvent.type === 'DND_EDITOR_SINGLE_ITEM' &&
          overEvent.type === 'DND_NAVIGATION_NO_CATEGORY' &&
          onMoveItemToNoCategory
        ) {
          return await onMoveItemToNoCategory(activeEvent.id);
        }

        if (
          activeEvent.type === 'DND_EDITOR_SINGLE_ITEM' &&
          ['DND_NAVIGATION_CATEGORY', 'DND_NAVIGATION_SUBCATEGORY'].includes(overEvent.type) &&
          onMoveItemsToCategory
        ) {
          return await onMoveItemsToCategory(activeEvent.id, overEvent.id);
        }

        if (
          activeEvent.type === 'DND_EDITOR_MULTIPLE_ITEMS' &&
          ['DND_NAVIGATION_CATEGORY', 'DND_NAVIGATION_SUBCATEGORY'].includes(overEvent.type) &&
          onMoveItemsToCategory &&
          activeEvent.selectedIds
        ) {
          return await onMoveItemsToCategory(activeEvent.id, overEvent.id, activeEvent.selectedIds);
        }

        if (
          activeEvent.type === 'DND_EDITOR_SINGLE_NO_CATEGORY_ITEM' &&
          ['DND_NAVIGATION_CATEGORY', 'DND_NAVIGATION_SUBCATEGORY'].includes(overEvent.type) &&
          onAssignNoCategoryItemsToCategory
        ) {
          return await onAssignNoCategoryItemsToCategory(activeEvent.id, overEvent.id);
        }

        if (
          activeEvent.type === 'DND_EDITOR_MULTIPLE_NO_CATEGORY_ITEMS' &&
          ['DND_NAVIGATION_CATEGORY', 'DND_NAVIGATION_SUBCATEGORY'].includes(overEvent.type) &&
          onAssignNoCategoryItemsToCategory &&
          activeEvent.selectedIds
        ) {
          return await onAssignNoCategoryItemsToCategory(activeEvent.id, overEvent.id, activeEvent.selectedIds);
        }
      } finally {
        reset();
      }
    },
    [activeEvent, onAssignNoCategoryItemsToCategory, onMoveItemToNoCategory, onMoveItemsToCategory, reset],
  );

  useDndMonitor({
    onDragStart,
    onDragEnd,
  });

  return (
    <>{children({ draggingId: activeEvent?.id, displayMenuEditorList: localMenuEditorList || menuEditorList || [] })}</>
  );
};
